// The nav tree for SWIFT SPA's left sidebar, in one place — SideNav.vue renders it.
//
// Ported from SwiftProcessingSPA's src/helpers/navConfig.js and adapted for SWIFT SPA:
// every route here is loan-scoped (the app carries the current loan via a `?id=<loanId>`
// query param, the way header.vue's old router-links did), so SideNav builds each link's
// `to` as a { path, query } object rather than a plain string.
//
// Item shape: { label, to, icon, gate? } — `to` is the route's base path.

function getRolesFromStorage(key) {
  let raw = null
  try {
    raw = window?.localStorage?.getItem(key)
  } catch (e) {}
  if (!raw) return []

  const toList = v =>
    String(v || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(x => String(x || "").trim()).filter(Boolean)
    return toList(parsed)
  } catch {
    return toList(raw)
  }
}

function rolesInclude(roles, role) {
  const target = String(role || "").toLowerCase().trim()
  return (roles || []).map(x => String(x || "").toLowerCase().trim()).includes(target)
}

// header.vue fetches the signed-in user's roles and stores them under "user_roles" (SWIFT SPA
// has no impersonation feature, so there's no separate "active_user_roles" like
// SwiftProcessingSPA has). header.vue emits "user-roles-ready" on the shared bus once that
// fetch resolves; SideNav re-reads permissions then, since it can mount before roles land.
export function computeNavPermissions() {
  const roles = getRolesFromStorage("user_roles")
  return {
    hasClosingRole: rolesInclude(roles, "Closing"),
    // Not a nav gate: Purchase Agreement shows its Field mapping editor to admins only.
    isAdmin: rolesInclude(roles, "Admin")
  }
}

export const NAV_ITEMS = [
  { label: "LoanFlow", to: "/LoanTasks", icon: "fa-light fa-route" },
  { label: "LoanLaunch", to: "/ConsumerPortal", icon: "fa-light fa-rocket-launch" },
  { label: "File Manager", to: "/FileManager", icon: "fa-light fa-folder-open" },
  { label: "Documents", to: "/Documents", icon: "fa-light fa-file-lines" },
  { label: "Prequal", to: "/Prequal", icon: "fa-light fa-file-certificate" },
  // Credit reports on the loan and ordering new ones (Xactus via Encompass Order Services).
  // Moved here from LoanFlow's task actions so it is a page, not a modal.
  { label: "Credit", to: "/Credit", icon: "fa-light fa-file-invoice-dollar" },
  // Polly's hosted pricer, framed. SideNav appends ?id=<loanId> like every other item here,
  // which is what puts Polly in Lock Mode for the open file rather than Scenario Mode.
  { label: "Pricing Engine", to: "/PricingEngine", icon: "fa-light fa-calculator" },
  // Loan-side rate lock: lock details for the open file, and requesting an extension. Sits
  // next to Pricing Engine because the two are the same job at different stages - price it,
  // then live with the lock. Ungated: anyone who can open the file can see its lock.
  { label: "Lock", to: "/Lock", icon: "fa-light fa-lock-keyhole" },
  { label: "Conditions", to: "/", icon: "fa-light fa-list-check" },
  { label: "Post Close", to: "/PostClose", icon: "fa-light fa-flag-checkered" },
  { label: "Duplicate", to: "/Duplicate", icon: "fa-light fa-copy" },
  // Purchase Agreement Import (9189), moved from SwiftProcessingSPA. Ungated: anyone on the file can
  // import its agreement (only editing the field mapping is Admin). Mirror any change in header.vue's mobile row.
  { label: "Purchase Agreement", to: "/PurchaseAgreement", icon: "fa-light fa-file-import" },
  // Hidden for now. Uncomment to bring it back - the /Closing route and ClosingView.vue are
  // untouched, so the page is still reachable by URL, it just isn't advertised in the sidebar.
  // { label: "Closing", to: "/Closing", icon: "fa-light fa-handshake", gate: "hasClosingRole" },
  { label: "SwiftData", to: "/SwiftData", icon: "fa-light fa-database" },
  { label: "Alerts", to: "/SwiftAlerts", icon: "fa-light fa-bell" }
]

// Routes are case-insensitive in vue-router; compare lowercase. "/" must be an exact match or
// it would light up on every page.
export function isPathActive(routePath, to) {
  const path = String(routePath || "").toLowerCase()
  const target = String(to || "").toLowerCase()
  if (target === "/") return path === "/"
  return path === target || path.startsWith(target + "/")
}

export function filterByPermissions(items, perms) {
  return items.filter(i => !i.gate || perms[i.gate])
}
